import { Link } from "react-router-dom";
import type { Movie } from "@/types/movie";
import { formatDuration, formatViews } from "@/lib/movies";
import FavoriteButton from "@/components/feature/FavoriteButton";

interface MovieCardProps {
  movie: Movie;
  showSynopsis?: boolean;
  rank?: number;
}

export default function MovieCard({ movie, showSynopsis = false, rank }: MovieCardProps) {
  return (
    <Link
      to={`/detail/${movie.id}`}
      className="group flex gap-3 rounded-lg outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary-400 sm:block"
    >
      <div className="relative aspect-[2/3] w-[104px] shrink-0 overflow-hidden rounded-lg bg-background-200 sm:w-full">
        <img
          src={movie.poster}
          alt={`${movie.title}（${movie.year}）电影海报`}
          title={`${movie.title} 在线观看 - 免费看大片`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/85 via-background-950/10 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

        {typeof rank === "number" && (
          <span className="absolute left-2 top-2 flex h-7 min-w-7 items-center justify-center rounded-md bg-accent-500 px-1.5 font-heading text-sm font-bold text-background-50">
            {rank}
          </span>
        )}

        <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-md bg-background-950/80 px-1.5 py-0.5 font-heading text-xs font-semibold text-accent-400 backdrop-blur-sm">
          <i className="ri-star-fill text-[10px]" />
          {movie.rating.toFixed(1)}
        </span>

        <span className="absolute inset-0 hidden items-center justify-center sm:flex">
          <span className="flex h-11 w-11 scale-90 items-center justify-center rounded-full bg-primary-500/90 text-background-50 opacity-0 transition-all duration-300 group-hover:scale-100 group-hover:opacity-100">
            <i className="ri-play-fill text-xl" />
          </span>
        </span>

        <div className="absolute bottom-2 left-2 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <FavoriteButton movieId={movie.id} variant="icon" />
        </div>
      </div>

      <div className="min-w-0 flex-1 sm:mt-3">
        <h3 className="line-clamp-1 font-heading text-sm font-medium text-foreground-50 transition-colors group-hover:text-primary-300 md:text-base">
          {movie.title}
        </h3>
        <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-foreground-500">
          <span>{movie.year}</span>
          <span className="text-foreground-700">·</span>
          <span>{movie.region}</span>
          <span className="hidden text-foreground-700 sm:inline">·</span>
          <span className="hidden sm:inline">{formatDuration(movie.duration)}</span>
        </p>
        <p className="mt-1 hidden flex-wrap items-center gap-x-1.5 text-xs text-foreground-500 sm:flex">
          <span>{movie.genres.slice(0, 2).join(" / ")}</span>
          <span className="text-foreground-700">·</span>
          <span>{formatViews(movie.views)}次播放</span>
        </p>
        {showSynopsis && (
          <p className="mt-1.5 hidden text-xs leading-relaxed text-foreground-500 sm:line-clamp-2">
            {movie.synopsis}
          </p>
        )}
      </div>
    </Link>
  );
}